import { Component, Inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

export type NeedsRxPath = 'CUSTOMER TO SUPPLY' | 'CALL FOR RX';

export interface HoldFollowUpDialogData {
  notes?: string;
  expectedDate?: string | Date;
  followUpDate?: string | Date;
  whoToCall?: string;
  initialPath?: NeedsRxPath | '';
}

export interface HoldRxPopupResult {
  path: NeedsRxPath;
  notes: string;
  expectedDate: Date | null;
  followUpDate: Date | null;
  examDate: Date | null;
  whoToCall: string;
}

@Component({
  selector: 'app-hold-follow-up-dialog',
  template: `
    <h2 mat-dialog-title>HOLD / RX</h2>
    <mat-dialog-content>
      <p class="hold-follow-up-message">
        Who is responsible for obtaining the prescription? Customer to supply
        uses the exam / expected date. Employee to call uses Who to Call and
        starts as Call Needed.
      </p>
      <mat-radio-group class="hold-rx-paths" [formControl]="path">
        <div class="hold-rx-row">
          <mat-radio-button value="CUSTOMER TO SUPPLY">
            CUSTOMER TO SUPPLY
          </mat-radio-button>
          <mat-form-field appearance="fill" class="hold-rx-field">
            <mat-label>EXAM DATE</mat-label>
            <input matInput [matDatepicker]="examPicker" [formControl]="examDate">
            <mat-datepicker-toggle matSuffix [for]="examPicker"></mat-datepicker-toggle>
            <mat-datepicker #examPicker></mat-datepicker>
          </mat-form-field>
        </div>
        <div class="hold-rx-row">
          <mat-radio-button value="CALL FOR RX">CALL FOR RX</mat-radio-button>
          <mat-form-field appearance="fill" class="hold-rx-field">
            <mat-label>WHO TO CALL</mat-label>
            <input matInput type="text" autocomplete="off" [formControl]="whoToCall">
          </mat-form-field>
        </div>
      </mat-radio-group>
      <mat-form-field appearance="fill" class="hold-follow-up-field">
        <mat-label>NOTES</mat-label>
        <textarea matInput rows="4" [formControl]="notes"></textarea>
      </mat-form-field>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button type="button" (click)="cancel()">CANCEL</button>
      <button
        mat-raised-button
        color="primary"
        type="button"
        [disabled]="!canSave"
        (click)="confirm()"
      >
        SAVE
      </button>
    </mat-dialog-actions>
  `,
  styles: [
    `
          .hold-follow-up-message {
            margin: 0 0 16px;
            font-size: 13px;
            line-height: 1.4;
          }
          .hold-rx-paths {
            display: block;
            margin-bottom: 12px;
          }
          .hold-rx-row {
            display: flex;
            align-items: center;
            gap: 16px;
            margin-bottom: 8px;
          }
          .hold-rx-row mat-radio-button {
            flex: 0 0 210px;
          }
          .hold-rx-field,
          .hold-follow-up-field {
            flex: 1 1 auto;
            width: 100%;
          }
        `,
  ],
})
export class HoldFollowUpDialogComponent {
  path = new FormControl('');
  notes = new FormControl('');
  examDate = new FormControl(null);
  whoToCall = new FormControl('');

  constructor(
    public dialogRef: MatDialogRef<
      HoldFollowUpDialogComponent,
      HoldRxPopupResult | null
    >,
    @Inject(MAT_DIALOG_DATA) public data: HoldFollowUpDialogData
  ) {
    this.notes.setValue(String(data?.notes || ''));
    this.whoToCall.setValue(String(data?.whoToCall || ''));
    this.examDate.setValue(
      this.parseDate(data?.expectedDate) ||
        this.parseDate(data?.followUpDate)
    );
    this.path.setValue(data?.initialPath || '');
  }

  get canSave(): boolean {
    if (this.path.value === 'CUSTOMER TO SUPPLY') {
      return !!this.parseDate(this.examDate.value);
    }
    return this.path.value === 'CALL FOR RX';
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  confirm(): void {
    if (!this.canSave) {
      return;
    }
    const exam = this.parseDate(this.examDate.value);
    const path = this.path.value as NeedsRxPath;
    this.dialogRef.close({
      path,
      notes: String(this.notes.value || '').trim(),
      expectedDate: path === 'CUSTOMER TO SUPPLY' ? exam : null,
      followUpDate: path === 'CUSTOMER TO SUPPLY' ? exam : new Date(),
      examDate: path === 'CUSTOMER TO SUPPLY' ? exam : null,
      whoToCall:
        path === 'CALL FOR RX' ? String(this.whoToCall.value || '').trim() : '',
    });
  }

  private parseDate(value?: string | Date | null): Date | null {
    if (!value) {
      return null;
    }
    const date = value instanceof Date ? value : new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }
}
